// Constant imports:
import { GET_SIGTI_API_URL } from '../../config';
// Service imports:
import { turnDateToSimpleTime } from '../../services/utils';
import { bugsnagNotify, fetchResponseToDetailedString } from '../../services/bugsnagUtils';
// Query imports:
import { addAlertReviewHistoryEntryQuery, modifyAlertConfigsQuery, requestAlertReviewHistoryQuery, requestAlertsConfigQuery, requestAlertsQuery } from '../queries/alertQueries';
// Type imports:
import { AppDispatch } from '../../index'; // Use the root dispatch type from your store
import { AlertStatus, IAlert, IAlertConfig, IAlertConfigParameterChange, IAlertDataUserInfo, IAlertReviewAttachment } from '../../types/api/AlertTypes';
import { AlertActions, REQUEST_ALERTS_WAITING, REQUEST_ALERTS_SUCCESS, REQUEST_ALERTS_FAILURE, REQUEST_ALERT_CONFIGS_WAITING, REQUEST_ALERT_CONFIGS_SUCCESS, REQUEST_ALERT_CONFIGS_FAILURE, MODIFY_ALERT_CONFIGS_SUCCESS, MODIFY_ALERT_CONFIGS_FAILURE, SET_SELECTED_STATUS, REQUEST_ALERT_REVIEW_HISTORY_WAITING, REQUEST_ALERT_REVIEW_HISTORY_SUCCESS, REQUEST_ALERT_REVIEW_HISTORY_FAILURE, ADD_ALERT_REVIEW_HISTORY_ENTRY_SUCCESS, ADD_ALERT_REVIEW_HISTORY_ENTRY_FAILURE } from '../../types/redux/AlertTypes';
import { REQUEST_FORBIDDEN } from '../../types/redux/LoginTypes';

// ================================ CHECK IF FORBIDDEN RESOURCE (LOGOUT IF TRUE) ================================
const checkIfForbiddenResource = (responseBody: any) => {
    if (responseBody.errors && responseBody.errors[0].message === "Forbidden resource") {
        return true;
    }
    return false;
}

const requestForbidden = () => ({
    type: REQUEST_FORBIDDEN,
});

/* REQUEST ALERTS */
const requestAlertsThunk = async (dispatch: AppDispatch, accessToken: string, fromDate: string, toDate: string) => {
    // Format time string.
    fromDate = turnDateToSimpleTime(new Date(fromDate));
    toDate = turnDateToSimpleTime(new Date(toDate));
    // Set waiting.
    dispatch(requestAlertsWaiting(fromDate, toDate));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestAlertsQuery();

    const queryObject = {
        query,
        variables: {
            fromDate: new Date(fromDate),
            toDate: new Date(toDate)
        }
    };

    // Build headers, only add Authorization if accessToken exists
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { alerts } = responseBody.data;
            dispatch(requestAlertsSuccess(fromDate, toDate, alerts) as AlertActions);
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Alerts', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestAlertsFailure(fromDate, toDate, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestAlertsFailure(fromDate, toDate, 'Network Error'));
    }
}

const requestAlertsWaiting = (fromDate: string, toDate: string): AlertActions => ({
    type: REQUEST_ALERTS_WAITING,
    payload: {
        fromDate,
        toDate
    }
});

const requestAlertsSuccess = (fromDate: string, toDate: string, alerts: Array<IAlert>): AlertActions => ({
    type: REQUEST_ALERTS_SUCCESS,
    payload: {
        fromDate,
        toDate,
        alerts
    }
});

const requestAlertsFailure = (fromDate: string, toDate: string, error: string): AlertActions => ({
    type: REQUEST_ALERTS_FAILURE,
    payload: {
        fromDate,
        toDate,
        error
    }
});

export const requestAlerts =
    (accessToken: string, fromDate: string, toDate: string) => 
    (dispatch: AppDispatch) =>
    requestAlertsThunk(dispatch, accessToken, fromDate, toDate);

/* REQUEST ALERTS CONFIG */
const requestAlertsConfigThunk = async (dispatch: AppDispatch, accessToken: string) => {
    // Set waiting.
    dispatch(requestAlertsConfigWaiting());
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestAlertsConfigQuery();

    const queryObject = {
        query,
        variables: {}
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { alertConfigs } = responseBody.data;
            dispatch(requestAlertsConfigSuccess(alertConfigs));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Alerts Config', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestAlertsConfigFailure('Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestAlertsConfigFailure('Network Error'));
    }
}

const requestAlertsConfigWaiting = (): AlertActions => ({
    type: REQUEST_ALERT_CONFIGS_WAITING
})

const requestAlertsConfigSuccess = (alertConfigs: Array<IAlertConfig>): AlertActions => ({
    type: REQUEST_ALERT_CONFIGS_SUCCESS,
    payload: {
        alertConfigs
    }
});

const requestAlertsConfigFailure = (error: string): AlertActions => ({
    type: REQUEST_ALERT_CONFIGS_FAILURE,
    payload: {
        error
    }
});

export const requestAlertsConfig =
    (accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestAlertsConfigThunk(dispatch, accessToken);


/* MODIFY ALERTS CONFIG */
const modifyAlertConfigsThunk = async (dispatch: AppDispatch, accessToken: string, changes: IAlertConfigParameterChange[]) => {
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = modifyAlertConfigsQuery();

    const configChangesAsGraphqlType = changes.map(({ alertId, paramId, newValue }) => ({
        alert_id: alertId,
        parameter_id: paramId,
        value: (typeof newValue === 'number') ? newValue : null,
        value_text: (typeof newValue === 'string') ? newValue : null
    }));

    const queryObject = {
        query,
        variables: {
            changeInputs: configChangesAsGraphqlType
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { addAlertConfigParameter: newAlertConfigs } = responseBody.data;
            dispatch(modifyAlertConfigsSuccess(newAlertConfigs));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Modify Alerts Config', response);
            bugsnagNotify(new Error(errorString));
            dispatch(modifyAlertConfigsFailure('Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(modifyAlertConfigsFailure('Network Error'));
    }
}

const modifyAlertConfigsSuccess = (newAlertConfigs: Array<IAlertConfig>): AlertActions => ({
    type: MODIFY_ALERT_CONFIGS_SUCCESS,
    payload: {
        newAlertConfigs
    }
});

const modifyAlertConfigsFailure = (error: string): AlertActions => ({
    type: MODIFY_ALERT_CONFIGS_FAILURE,
    payload: {
        error
    }
});

export const modifyAlertConfigs =
    (accessToken: string, changes: IAlertConfigParameterChange[]) =>
    (dispatch: AppDispatch) =>
    modifyAlertConfigsThunk(dispatch, accessToken, changes);

/* SET SELECTED STATUSES */
export const setSelectedStatus =
    (status: AlertStatus | null, skipPushingToHistory?: boolean) =>
    (dispatch: AppDispatch) =>
    dispatch({
        type: SET_SELECTED_STATUS,
        payload: {
            status,
            skipPushingToHistory: skipPushingToHistory ?? false
        }
    });

/* REQUEST ALERT REVIEW HISTORY */
const requestAlertReviewHistoryThunk = async (dispatch: AppDispatch, accessToken: string, alertDataId: number) => {
    // Set waiting.
    dispatch(requestAlertReviewHistoryWaiting(alertDataId));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestAlertReviewHistoryQuery();

    const queryObject = {
        query,
        variables: {
            alertDataId
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { alertReviewHistory } = responseBody.data;
            dispatch(requestAlertReviewHistorySuccess(alertDataId, alertReviewHistory));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Alert Review History', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestAlertReviewHistoryFailure(alertDataId, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestAlertReviewHistoryFailure(alertDataId, 'Network Error'));
    }
}

const requestAlertReviewHistoryWaiting = (alertDataId: number): AlertActions => ({
    type: REQUEST_ALERT_REVIEW_HISTORY_WAITING,
    payload: {
        alertDataId
    }
});

const requestAlertReviewHistorySuccess = (alertDataId: number, alertReviewHistory: Array<IAlertDataUserInfo>): AlertActions => ({
    type: REQUEST_ALERT_REVIEW_HISTORY_SUCCESS,
    payload: {
        alertDataId,
        alertReviewHistory
    }
});

const requestAlertReviewHistoryFailure = (alertDataId: number, error: string): AlertActions => ({
    type: REQUEST_ALERT_REVIEW_HISTORY_FAILURE,
    payload: {
        alertDataId,
        error
    }
});

export const requestAlertReviewHistory =
    (accessToken: string, alertDataId: number) => 
    (dispatch: AppDispatch) =>
    requestAlertReviewHistoryThunk(dispatch, accessToken, alertDataId);

/* ADD ALERT REVIEW HISTORY ENTRY */
const addAlertReviewHistoryEntryThunk = async (
    dispatch: AppDispatch,
    accessToken: string,
    alertDataIds: number[],
    comment: string,
    newStatus: AlertStatus,
    attachments?: IAlertReviewAttachment[]
) => {
    const statusMap: {
        [key in AlertStatus]: string | null;
      } = {
        'forreview': null,
        'open': 'Open',
        'watch': 'Watch',
        'investig': 'Investigating',
        'critical': 'Critical',
        'explained': 'Explained',
        'closed': 'Closed'
      }
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = addAlertReviewHistoryEntryQuery();

    const alertReviewInput = {
        alert_ids: alertDataIds,
        status: statusMap[newStatus],
        comment,
        attachments // pass attachments to backend if present
    }

    const queryObject = {
        query,
        variables: {
            alertReviewInput
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { addReviewHistory: newReviewHistoryEntries } = responseBody.data;
            dispatch(addAlertReviewHistoryEntrySuccess(newReviewHistoryEntries));
            return newReviewHistoryEntries;
        } else {
            const { errorString } = await fetchResponseToDetailedString('Add Alert Review History Entry', response);
            bugsnagNotify(new Error(errorString));
            dispatch(addAlertReviewHistoryEntryFailure('Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(addAlertReviewHistoryEntryFailure('Network Error'));
    }
}

const addAlertReviewHistoryEntrySuccess = (newAlertReviewHistoryEntries: Array<IAlertDataUserInfo>): AlertActions => ({
    type: ADD_ALERT_REVIEW_HISTORY_ENTRY_SUCCESS,
    payload: {
        newAlertReviewHistoryEntries
    }
});

const addAlertReviewHistoryEntryFailure = (error: string): AlertActions => ({
    type: ADD_ALERT_REVIEW_HISTORY_ENTRY_FAILURE,
    payload: {
        error
    }
});

export const addAlertReviewHistoryEntry =
    (
        accessTokenV2: string,
        alertDataIds: number[],
        comment: string,
        newStatus: AlertStatus,
        attachments?: IAlertReviewAttachment[]
    ) =>
    (dispatch: AppDispatch) =>
    addAlertReviewHistoryEntryThunk(dispatch, accessTokenV2, alertDataIds, comment, newStatus, attachments);
